// The collector deliberately has no dependency on Google, Meta, GTM or cookies.
export function validValue(value, rule) {
  if (!rule) return false
  switch (rule.type) {
    case 'enum': return typeof value === 'string' && rule.values.includes(value)
    case 'identifier': return typeof value === 'string' && /^[a-zA-Z][a-zA-Z0-9_-]{0,63}$/.test(value)
    case 'country': return typeof value === 'string' && /^[A-Z]{2}$/.test(value)
    case 'currency': return typeof value === 'string' && /^[A-Z]{3}$/.test(value)
    case 'boolean': return typeof value === 'boolean'
    case 'integer': return Number.isSafeInteger(value) && value >= 0 && value <= (rule.max || 1000000)
    case 'number': return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 100000000
    default: return false
  }
}

export function safeProperties(manifest, name, values, source = 'browser') {
  const definition = manifest.events[name]
  if (!definition || !definition.sources.includes(source)) return null
  if (!values || typeof values !== 'object' || Array.isArray(values)) return null
  for (const [key, value] of Object.entries(values)) {
    if (!definition.parameters.includes(key) || !validValue(value, manifest.parameters[key])) return null
  }
  return values
}

export function completionState(components) {
  const filled = components.filter(component => String(component.value || '').trim() !== '').length
  return filled === 0 ? 'empty' : filled === components.length ? 'filled' : 'partial'
}

export function validityCategory(components) {
  if (components.some(component => component.validity && component.validity.valueMissing)) return 'missing'
  if (components.some(component => component.validity && !component.validity.valid)) return 'invalid'
  return null
}

export function createCollector({ manifest, transport, storage, uuid, now = () => Date.now(), onEvent = () => {} }) {
  let config = { enabled: false }
  let queue = []
  let sequence = 0
  let sending = false
  let generation = 0
  const ttl = 24 * 60 * 60 * 1000
  const maxQueue = 400
  const storageKey = 'mymalls_measurement_v1'
  const persist = () => {
    try { storage?.setItem(storageKey, JSON.stringify({ journey: config.journey_id, sequence, queue })) } catch (_) { /* Memory still works. */ }
  }
  const prune = () => { queue = queue.filter(event => now() - Date.parse(event.occurred_at) < ttl).slice(-maxQueue) }
  return {
    configure(next) {
      if (!next || next.schema_version !== manifest.schema_version || !(next.enabled || next.platform_enabled)) next = { enabled: false }
      next = { ...next, active: !!(next.enabled || next.platform_enabled) }
      if (next.journey_id !== config.journey_id || !next.enabled) {
        generation += 1
        queue = []
        sequence = 0
        if (next.enabled) {
          try {
            const saved = JSON.parse(storage?.getItem(storageKey) || 'null')
            if (saved?.journey === next.journey_id && Array.isArray(saved.queue)) {
              queue = saved.queue.filter(event => /^[0-9a-f-]{36}$/i.test(event.event_id) &&
                Number.isSafeInteger(event.sequence) && event.sequence > 0 &&
                event.schema_version === manifest.schema_version &&
                safeProperties(manifest, event.event_name, event.properties))
              sequence = Math.max(Number.isSafeInteger(saved.sequence) && saved.sequence > 0 ? saved.sequence : 0, ...queue.map(event => event.sequence))
            }
          } catch (_) { /* An unavailable or corrupt store cannot affect the form. */ }
        }
      }
      config = next
      prune()
      persist()
    },
    get config() { return config },
    get pending() { return queue.slice() },
    emit(name, properties = {}) {
      if (!config.active) return null
      const values = { ...(config.context || {}), screen_name: config.screen_name, ...properties }
      if (!safeProperties(manifest, name, values)) return null
      const event = { event_id: uuid(), event_name: name, schema_version: manifest.schema_version,
        occurred_at: new Date(now()).toISOString(), sequence: ++sequence, properties: values }
      if (config.enabled) { queue.push(event); prune(); persist() }
      try { onEvent(event, config) } catch (_) { /* Destination tags are optional. */ }
      return event
    },
    async flush({ keepalive = false } = {}) {
      if (sending || !config.enabled || queue.length === 0) return
      prune()
      const batch = []
      const envelope = { journey_id: config.journey_id, events: batch }
      for (const event of queue.slice(0, 40)) {
        batch.push(event)
        // UTF-8 byte bound, including the envelope; fetch keepalive has a global limit.
        if (new TextEncoder().encode(JSON.stringify(envelope)).length > 30000) { batch.pop(); break }
      }
      if (!batch.length) return
      const sentGeneration = generation
      sending = true
      try {
        const response = await transport(config.endpoint, envelope, { keepalive })
        if (sentGeneration !== generation) return
        if (response?.suppressed) { this.configure({ enabled: false }); return }
        const acknowledged = new Set(Array.isArray(response?.accepted) ? response.accepted : [])
        queue = queue.filter(event => !acknowledged.has(event.event_id))
        persist()
      } catch (_) { /* Retain the same IDs and timestamps for retry. */ }
      finally { sending = false }
    }
  }
}
